import type { ReactElement } from 'react';
import type { MediaTrafficCounters, MediaTrafficDto } from '@gr/core/content/media/dto';
import { useT } from '../../../../app/di';
import { formatDate, formatFileSize, formatNumber } from '../../../../shared/format';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Card } from '../../../../ui-kit/Card/Card';
import { DataTable, type DataColumn } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { ProgressBar } from '../../../../ui-kit/ProgressBar/ProgressBar';

/** MED-UC-7, MED-RULE-2: трафик медиа блога за месяц — всего, по видам файлов и по дням. */
export function MediaTrafficReport({ report }: { report: MediaTrafficDto }): ReactElement {
  const { t, locale } = useT();
  const size = (bytes: number): string => formatFileSize(bytes, locale);
  const used = size(report.monthBytes);
  const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${report.month}-01T00:00:00Z`));
  const limitReached = report.monthLimitBytes !== null && report.monthBytes >= report.monthLimitBytes;
  const counters = <R extends MediaTrafficCounters>(): DataColumn<R>[] => [
    { key: 'requests', header: t('media.traffic.requests'), align: 'end', cell: (row) => formatNumber(row.requests, locale) },
    { key: 'bytes', header: t('media.traffic.bytes'), align: 'end', cell: (row) => size(row.bytes) },
    { key: 'browser', header: t('media.traffic.browserCache'), align: 'end', cell: (row) => formatNumber(row.notModified, locale) },
    { key: 'server', header: t('media.traffic.serverCache'), align: 'end', cell: (row) => formatNumber(row.cacheHits, locale) },
  ];

  return (
    <div className="u-stack-lg">
      <Card title={t('media.traffic.month', { month })}>
        <div className="u-stack">
          <p className="u-muted u-small">{t('media.traffic.intro')}</p>
          <strong>{report.monthLimitBytes === null ? t('media.traffic.noLimit', { used }) : t('media.traffic.limit', { used, limit: size(report.monthLimitBytes) })}</strong>
          {report.monthLimitBytes !== null ? <ProgressBar value={report.monthBytes / report.monthLimitBytes} label={t('media.traffic.month', { month })} /> : null}
          {limitReached ? <Alert tone="warning">{t('media.traffic.limitReached')}</Alert> : null}
        </div>
      </Card>
      <Card title={t('media.traffic.byKind')}>
        <DataTable
          label={t('media.traffic.byKind')}
          rows={report.byKind}
          rowKey={(row) => row.kind}
          columns={[{ key: 'kind', header: t('media.traffic.kind'), primary: true, cell: (row) => t(`media.traffic.kinds.${row.kind}`) }, ...counters<(typeof report.byKind)[number]>()]}
        />
      </Card>
      <Card title={t('media.traffic.byDay')}>
        <DataTable
          label={t('media.traffic.byDay')}
          rows={report.days}
          rowKey={(row) => row.day}
          empty={<EmptyState title={t('media.traffic.empty')} />}
          columns={[{ key: 'day', header: t('media.traffic.day'), primary: true, cell: (row) => formatDate(`${row.day}T12:00:00Z`, locale, 'UTC') }, ...counters<(typeof report.days)[number]>()]}
        />
      </Card>
    </div>
  );
}
